package com.essntl.features.proposal.ui.selectservice

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.PaginationState
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.collectSuccessEvent
import com.essntl.core.utils.viewmodel.getData
import com.essntl.core.utils.viewmodel.getPaginationData
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.service.domain.model.service.ServiceFilterOptions
import com.essntl.features.service.domain.use_case.ExtraCategoryServiceUseCase
import com.essntl.features.supplier.domain.repository.SupplierRepository
import com.essntl.features.tag.domain.repository.TagRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SelectServiceViewModel(
    private val tagRepository: TagRepository,
    private val categoryRepository: CategoryRepository,
    private val supplierRepository: SupplierRepository,
    private val areaRepository: AreaRepository,
    private val extraCategoryServiceUseCase: ExtraCategoryServiceUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    private val state: MutableStateFlow<SelectServiceState>,
) : ViewModel {

    init {
        getAllCategory()
        getAllTag()
        getAllSupplier()
        getAllArea()
        getCurrentProfile()

        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getCurrentProfile },
            setState = { newState -> state.update { it.copy(getCurrentProfile = newState) } },
        )
    }

    private fun getCurrentProfile() = scope.launch { getCurrentUserUseCase.get() }


    fun onEvent(event: SelectServiceEvent) {
        when (event) {
            is SelectServiceEvent.ProposalServiceChanged -> proposalServiceChanged(event)
            is SelectServiceEvent.OpenProposalPreview -> openProposalPreview(event)
            is SelectServiceEvent.AddService -> addService(event)
            is SelectServiceEvent.EditService -> editService(event)
            is SelectServiceEvent.FilterServices -> editFilterOptions(event.filterOptions)
            is SelectServiceEvent.GetAllService -> getAllService()
            is SelectServiceEvent.LoadMoreServices -> loadMoreServices()
        }
    }

    private fun getAllService() {
        state.update {
            it.copy(getAllService = PaginationState.loading())
        }

        loadMoreServices(ignoreLoading = true)
    }

    private fun loadMoreServices(ignoreLoading: Boolean = false) =
        getPaginationData(
            getData = {
                val nextPage = state.value.getAllService.loadedPages + 1L
                extraCategoryServiceUseCase
                    .getAllNonExtraServices(
                        page = nextPage,
                        filterOptions = state.value.filterOptions,
                    )
                    .map { list ->
                        list.filter { service ->
                            state.value.getAllService.data.none { it.id == service.id }
                        }
                    }
            },
            getState = { state.value.getAllService },
            setState = { newState -> state.update { it.copy(getAllService = newState) } },
            ignoreLoading = ignoreLoading,
        )

    private fun getAllCategory() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { newState -> state.value = state.value.copy(getAllCategory = newState) },
        )

    private fun getAllTag() =
        getData(
            getData = { tagRepository.getAll() },
            getState = { state.value.getAllTag },
            setState = { newState -> state.value = state.value.copy(getAllTag = newState) },
        )

    private fun getAllSupplier() =
        getData(
            getData = { supplierRepository.getAll() },
            getState = { state.value.getAllSupplier },
            setState = { newState -> state.value = state.value.copy(getAllSupplier = newState) },
        )

    private fun getAllArea() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { newState -> state.value = state.value.copy(getAllArea = newState) },
        )

    private fun proposalServiceChanged(event: SelectServiceEvent.ProposalServiceChanged) =
        state.update {
            it.copy(
                previewLink = null,
            )
        }.also {
            state.value.selectedProposalService.value = event.proposalService
        }

    private fun openProposalPreview(event: SelectServiceEvent.OpenProposalPreview) =
        state.update {
            it.copy(
                previewLink = event.previewLink,
            )
        }

    private fun addService(event: SelectServiceEvent.AddService) = getAllService()

    private fun editService(event: SelectServiceEvent.EditService) {
        if (state.value.getAllService.data.any { it.id == event.service.id })
            state.update { s ->
                s.copy(
                    getAllService = s.getAllService.copy(
                        data = s.getAllService.data
                            .toMutableList()
                            .apply {
                                indexOfFirst { it.id == event.service.id }
                                    .takeIf { it != -1 }
                                    ?.let { set(it, event.service) }
                            }
                            .toList(),
                    ),
                )
            }
        else
            getAllService()
    }

    private fun editFilterOptions(filterOptions: ServiceFilterOptions) {
        if (filterOptions == state.value.filterOptions) return

        state.update {
            it.copy(
                filterOptions = filterOptions,
            )
        }

        getAllService()
    }
}